import { useIndexedDb, STORES } from "@shared/storage/useIndexedDb";

import { useKeyWrap } from "./useKeyWrap";

// touchid — только Tauri на macOS: обёрточный секрет в Keychain за биометрией
// (см. useTouchId), в IDB — лишь обёртки ключей под этим методом.
export type AuthMethod = "prf" | "pin" | "touchid";

export function useKeyStorage() {
    const { read, write, remove } = useIndexedDb(STORES.KEYS);
    const { encryptKey, decryptKey, encryptRawKey, decryptRawKey } =
        useKeyWrap();

    async function hasKeys(): Promise<boolean> {
        return (await read("auth_methods")) !== null;
    }

    async function getAuthMethods(): Promise<AuthMethod[]> {
        return (await read<AuthMethod[]>("auth_methods")) ?? [];
    }

    async function setAuthMethods(methods: AuthMethod[]): Promise<void> {
        await write(methods, "auth_methods");
    }

    async function getCredentialId(): Promise<ArrayBuffer | null> {
        return read<ArrayBuffer>("webauthn_credential_id");
    }

    async function storeCredentialId(rawId: Uint8Array): Promise<void> {
        await write(rawId, "webauthn_credential_id");
    }

    async function getPinSalt(): Promise<Uint8Array<ArrayBuffer> | null> {
        return read<Uint8Array<ArrayBuffer>>("pin_salt");
    }

    async function storePinSalt(salt: Uint8Array<ArrayBuffer>): Promise<void> {
        await write(salt, "pin_salt");
    }

    async function storeEncryptedKeys(
        ecdhPair: CryptoKeyPair,
        signPair: CryptoKeyPair,
        wrappingKey: CryptoKey,
        method: AuthMethod
    ): Promise<void> {
        const [ecdhEnc, signEnc] = await Promise.all([
            encryptKey(ecdhPair.privateKey, wrappingKey),
            encryptKey(signPair.privateKey, wrappingKey),
        ]);
        await Promise.all([
            write(ecdhEnc.data, `ecdh_enc_${method}`),
            write(ecdhEnc.iv, `ecdh_iv_${method}`),
            write(signEnc.data, `sign_enc_${method}`),
            write(signEnc.iv, `sign_iv_${method}`),
        ]);
    }

    // Ключ устройства (#5) — обёрнут тем же wrappingKey, что и приватные ключи,
    // и хранится под каждый метод входа, чтобы был доступен независимо от того,
    // разблокировались через PRF или PIN.
    async function storeEncryptedStorageKey(
        storageKey: CryptoKey,
        wrappingKey: CryptoKey,
        method: AuthMethod
    ): Promise<void> {
        const enc = await encryptRawKey(storageKey, wrappingKey);
        await Promise.all([
            write(enc.data, `storage_enc_${method}`),
            write(enc.iv, `storage_iv_${method}`),
        ]);
    }

    async function loadStorageKey(
        wrappingKey: CryptoKey,
        method: AuthMethod
    ): Promise<CryptoKey | null> {
        const [data, iv] = await Promise.all([
            read<ArrayBuffer>(`storage_enc_${method}`),
            read<ArrayBuffer>(`storage_iv_${method}`),
        ]);
        if (!data || !iv) {
            return null;
        }
        return decryptRawKey(data, iv, wrappingKey);
    }

    async function storePublicKeys(
        ecdhPair: CryptoKeyPair,
        signPair: CryptoKeyPair
    ): Promise<void> {
        const [ecdhPub, signPub] = await Promise.all([
            crypto.subtle.exportKey("raw", ecdhPair.publicKey),
            crypto.subtle.exportKey("raw", signPair.publicKey),
        ]);
        await Promise.all([
            write(ecdhPub, "ecdh_pub"),
            write(signPub, "sign_pub"),
        ]);
    }

    async function loadKeys(
        wrappingKey: CryptoKey,
        method: AuthMethod
    ): Promise<{ ecdh: CryptoKeyPair; sign: CryptoKeyPair }> {
        const [ecdhEnc, ecdhIv, signEnc, signIv, ecdhPub, signPub] =
            await Promise.all([
                read<ArrayBuffer>(`ecdh_enc_${method}`),
                read<ArrayBuffer>(`ecdh_iv_${method}`),
                read<ArrayBuffer>(`sign_enc_${method}`),
                read<ArrayBuffer>(`sign_iv_${method}`),
                read<ArrayBuffer>("ecdh_pub"),
                read<ArrayBuffer>("sign_pub"),
            ]);

        if (
            !ecdhEnc ||
            !ecdhIv ||
            !signEnc ||
            !signIv ||
            !ecdhPub ||
            !signPub
        ) {
            throw new Error("Incomplete key storage");
        }

        const [ecdhPrivate, signPrivate, ecdhPublic, signPublic] =
            await Promise.all([
                decryptKey(
                    ecdhEnc,
                    ecdhIv,
                    wrappingKey,
                    { name: "ECDH", namedCurve: "P-256" },
                    ["deriveKey"]
                ),
                decryptKey(
                    signEnc,
                    signIv,
                    wrappingKey,
                    { name: "ECDSA", namedCurve: "P-256" },
                    ["sign"]
                ),
                crypto.subtle.importKey(
                    "raw",
                    ecdhPub,
                    { name: "ECDH", namedCurve: "P-256" },
                    true,
                    []
                ),
                crypto.subtle.importKey(
                    "raw",
                    signPub,
                    { name: "ECDSA", namedCurve: "P-256" },
                    true,
                    ["verify"]
                ),
            ]);

        return {
            ecdh: { privateKey: ecdhPrivate, publicKey: ecdhPublic },
            sign: { privateKey: signPrivate, publicKey: signPublic },
        };
    }

    // Полная чистка данных метода входа (обёртки ключей + его артефакты).
    // Keychain-секрет touchid чистит вызывающий (он вне IDB).
    async function removeMethodData(method: AuthMethod): Promise<void> {
        const keys = [
            `ecdh_enc_${method}`,
            `ecdh_iv_${method}`,
            `sign_enc_${method}`,
            `sign_iv_${method}`,
            `storage_enc_${method}`,
            `storage_iv_${method}`,
        ];
        if (method === "pin") {
            keys.push("pin_salt");
        }
        if (method === "prf") {
            keys.push("webauthn_credential_id");
        }
        await Promise.all(keys.map((k) => remove(k)));
    }

    return {
        hasKeys,
        getAuthMethods,
        setAuthMethods,
        removeMethodData,
        getCredentialId,
        storeCredentialId,
        getPinSalt,
        storePinSalt,
        storeEncryptedKeys,
        storeEncryptedStorageKey,
        loadStorageKey,
        storePublicKeys,
        loadKeys,
    };
}
